/**
 * Invoice Redux reducer following CourierBoost patterns
 */

import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap';
import type { InterfaceGenericTypeSlug } from '@interfaces/InterfaceGenericTypeSlug';
import {
  type InterfaceCreateInvoiceForm,
  type InterfaceInvoice,
  type InterfaceInvoiceFilters,
  type InterfaceInvoiceLineItem,
  type InterfaceInvoicePayment,
  type InterfaceInvoiceType,
  type InterfaceInvoiceUrlVars,
  type InterfaceRecordPaymentForm,
  type InterfaceUpdateInvoiceForm,
  InvoiceStatus,
} from '@interfaces/InterfaceInvoice';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore';
import { meLogOut } from '@store/reducers/meReducer';
import { addSuccessNotification } from '@store/reducers/notificationReducer';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut';

export interface InvoiceReducerState {
  types: InterfaceGenericMap<InterfaceInvoiceType>;
  paymentTermsTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  invoices: InterfaceGenericMap<InterfaceInvoice>;
  lineItems: InterfaceGenericMap<InterfaceInvoiceLineItem>;
  payments: InterfaceGenericMap<InterfaceInvoicePayment>;
  filters: InterfaceInvoiceFilters;
  selectedInvoiceUuid: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: InvoiceReducerState = {
  types: {},
  paymentTermsTypes: {},
  invoices: {},
  lineItems: {},
  payments: {},
  filters: {
    page: 1,
    perPage: 25,
    sortBy: 'invoiceDate',
    sortOrder: 'desc',
  },
  selectedInvoiceUuid: null,
  status: 'idle',
  error: null,
};

export const sliceName = 'invoice';

export const invoiceSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setSelectedInvoice: (state, action) => {
      state.selectedInvoiceUuid = action.payload;
    },
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder
      // Reset on logout
      .addCase(meLogOut.fulfilled, () => initialState)

      // Get invoice types
      .addCase(getInvoiceTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('types'))

      // Get payment terms types
      .addCase(getPaymentTermsTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('paymentTermsTypes'))

      // Get invoices
      .addCase(getInvoices.fulfilled, sliceStateCheckAndUpdate.sliceItem('invoices'))
      .addCase(getInvoices.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to load invoices';
      })

      // Get single invoice
      .addCase(getInvoice.fulfilled, sliceStateCheckAndUpdate.sliceItemElementUuid('invoices'))

      // Create invoice
      .addCase(createInvoice.fulfilled, (state, action) => {
        const invoice = action.payload;
        state.invoices[invoice.uuid] = invoice;

        // Add line items
        invoice.lineItems.forEach((item: InterfaceInvoiceLineItem) => {
          state.lineItems[item.uuid] = item;
        });
      })

      // Update invoice
      .addCase(updateInvoice.fulfilled, sliceStateCheckAndUpdate.sliceItemElementUuid('invoices'))

      // Delete invoice
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        if (action.meta.arg?.urlVarsReplaceMap) {
          const { invoiceUuid } = action.meta.arg.urlVarsReplaceMap as unknown as Record<string, string>;
          if (!invoiceUuid) {
            return;
          }

          // Remove invoice
          delete state.invoices[invoiceUuid];

          // Remove associated line items
          Object.values(state.lineItems).forEach((item) => {
            if (item.invoiceUuid === invoiceUuid) {
              delete state.lineItems[item.uuid];
            }
          });

          // Remove associated payments
          Object.values(state.payments).forEach((payment) => {
            if (payment.invoiceUuid === invoiceUuid) {
              delete state.payments[payment.uuid];
            }
          });

          // Clear selection if deleted
          if (state.selectedInvoiceUuid === invoiceUuid) {
            state.selectedInvoiceUuid = null;
          }
        }
      })

      // Record payment
      .addCase(recordPayment.fulfilled, (state, action) => {
        const { payment } = action.payload;
        const { invoice } = action.payload;

        // Update payment record
        state.payments[payment.uuid] = payment;

        // Update invoice with new totals
        state.invoices[invoice.uuid] = invoice;
      })

      // Update invoice status
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        if (action.meta.arg?.urlVarsReplaceMap && action.meta.arg?.form) {
          const { invoiceUuid } = action.meta.arg.urlVarsReplaceMap as unknown as Record<string, string>;
          const { status } = action.meta.arg.form;
          if (invoiceUuid && state.invoices[invoiceUuid] && status) {
            state.invoices[invoiceUuid].status = status;
          }
        }
      })

      // Send invoice
      .addCase(sendInvoice.fulfilled, (state, action) => {
        const invoice = action.payload;
        state.invoices[invoice.uuid] = invoice;
      })

      // Get invoice payments
      .addCase(getInvoicePayments.fulfilled, (state, action) => {
        const payments = action.payload;
        payments.forEach((payment: InterfaceInvoicePayment) => {
          state.payments[payment.uuid] = payment;
        });
      });
  },
});

// Actions
export const { setSelectedInvoice, updateFilters, clearFilters } = invoiceSlice.actions;

// Thunks
export const getInvoiceTypes = thunkGet.get(sliceName, 'type/invoice');
export const getPaymentTermsTypes = thunkGet.get(sliceName, 'type/payment_terms');

export const getInvoices = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/invoice');

export const getInvoice = thunkGet.compUrl<InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}',
);

export const createInvoice = thunkPost.compUrl<InterfaceCreateInvoiceForm>(
  sliceName,
  '/c/{companySlug}/admin/invoice',
  [
    addSuccessNotification({
      title: 'Invoice Created',
      message: 'Invoice has been created successfully',
    }),
  ],
);

export const updateInvoice = thunkPut.compUrl<InterfaceUpdateInvoiceForm, InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}',
  [
    addSuccessNotification({
      title: 'Invoice Updated',
      message: 'Invoice has been updated successfully',
    }),
  ],
);

export const deleteInvoice = thunkDelete.compUrl<InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}',
  [
    addSuccessNotification({
      title: 'Invoice Deleted',
      message: 'Invoice has been deleted successfully',
    }),
  ],
);

export const recordPayment = thunkPost.compUrl<InterfaceRecordPaymentForm, InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}/payment',
  [
    addSuccessNotification({
      title: 'Payment Recorded',
      message: 'Payment has been recorded successfully',
    }),
  ],
);

export const updateInvoiceStatus = thunkPut.compUrl<{ status: InvoiceStatus }, InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}/status',
  [
    addSuccessNotification({
      title: 'Status Updated',
      message: 'Invoice status has been updated',
    }),
  ],
);

export const sendInvoice = thunkPost.compUrl<InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}/send',
  [
    addSuccessNotification({
      title: 'Invoice Sent',
      message: 'Invoice has been sent to the customer',
    }),
  ],
);

export const getInvoicePayments = thunkGet.compUrl<InterfaceInvoiceUrlVars>(
  sliceName,
  '/c/{companySlug}/admin/invoice/{invoiceUuid}/payments',
);

// Base selectors
export const selectInvoiceState = (state: AppState) => state.invoice;
export const selectInvoiceTypes = (state: AppState) => selectInvoiceState(state).types;
export const selectPaymentTermsTypes = (state: AppState) => selectInvoiceState(state).paymentTermsTypes;
export const selectInvoices = (state: AppState) => selectInvoiceState(state).invoices;
export const selectInvoiceLineItems = (state: AppState) => selectInvoiceState(state).lineItems;
export const selectInvoicePayments = (state: AppState) => selectInvoiceState(state).payments;
export const selectInvoiceFilters = (state: AppState) => selectInvoiceState(state).filters;
export const selectSelectedInvoiceUuid = (state: AppState) => selectInvoiceState(state).selectedInvoiceUuid;

// Memoized selectors
export const selectInvoiceByUuid = createSelector(
  [selectInvoices, (_state: AppState, uuid?: string) => uuid],
  (invoices, uuid) => (uuid ? invoices[uuid] : undefined),
);

export const selectSelectedInvoice = createSelector(
  [selectInvoices, selectSelectedInvoiceUuid],
  (invoices, selectedUuid) => (selectedUuid ? invoices[selectedUuid] : null),
);

export const selectInvoicesList = createSelector([selectInvoices], (invoices) => Object.values(invoices));

export const selectFilteredInvoices = createSelector(
  [selectInvoicesList, selectInvoiceFilters],
  (invoices, filters) => {
    let filtered = [...invoices];

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((invoice) => invoice.status === filters.status);
    }

    // Apply customer filter
    if (filters.customerUuid) {
      filtered = filtered.filter((invoice) => invoice.customerUuid === filters.customerUuid);
    }

    // Apply date range filter
    if (filters.dateFrom) {
      const { dateFrom } = filters;
      filtered = filtered.filter((invoice) => invoice.invoiceDate >= dateFrom);
    }
    if (filters.dateTo) {
      const { dateTo } = filters;
      filtered = filtered.filter((invoice) => invoice.invoiceDate <= dateTo);
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (invoice) =>
          invoice.invoiceNumber.toLowerCase().includes(searchLower) ||
          invoice.customerName.toLowerCase().includes(searchLower) ||
          invoice.customerEmail?.toLowerCase().includes(searchLower),
      );
    }

    // Apply sorting
    const { sortBy } = filters || 'invoiceDate';
    const { sortOrder } = filters || 'desc';

    filtered.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortBy) {
        case 'invoiceNumber':
          aVal = a.invoiceNumber;
          bVal = b.invoiceNumber;
          break;
        case 'invoiceDate':
          aVal = a.invoiceDate;
          bVal = b.invoiceDate;
          break;
        case 'dueDate':
          aVal = a.dueDate;
          bVal = b.dueDate;
          break;
        case 'total':
          aVal = a.total;
          bVal = b.total;
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
      }

      if (aVal < bVal) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  },
);

export const selectInvoiceLineItemsByInvoice = createSelector(
  [selectInvoiceLineItems, (_state: AppState, invoiceUuid: string) => invoiceUuid],
  (lineItems, invoiceUuid) =>
    Object.values(lineItems)
      .filter((item) => item.invoiceUuid === invoiceUuid)
      .sort((a, b) => a.sortOrder - b.sortOrder),
);

export const selectInvoicePaymentsByInvoice = createSelector(
  [selectInvoicePayments, (_state: AppState, invoiceUuid: string) => invoiceUuid],
  (payments, invoiceUuid) =>
    Object.values(payments)
      .filter((payment) => payment.invoiceUuid === invoiceUuid)
      .sort((a, b) => b.paymentDate.localeCompare(a.paymentDate)),
);

// Statistics selectors
export const selectInvoiceStatistics = createSelector([selectInvoicesList], (invoices) => {
  const stats = {
    total: invoices.length,
    byStatus: {} as Record<InvoiceStatus, number>,
    totalAmount: 0,
    totalPaid: 0,
    totalDue: 0,
  };

  // Initialize status counts
  Object.values(InvoiceStatus).forEach((status) => {
    stats.byStatus[status] = 0;
  });

  // Calculate statistics
  invoices.forEach((invoice) => {
    stats.byStatus[invoice.status]++;
    stats.totalAmount += invoice.total;
    stats.totalPaid += invoice.amountPaid;
    stats.totalDue += invoice.amountDue;
  });

  return stats;
});

// Payment terms options selector
export const selectPaymentTermsOptions = createSelector([selectPaymentTermsTypes], (paymentTermsTypes) =>
  Object.values(paymentTermsTypes).map((term) => ({
    value: term.slug,
    label: term.name,
  })),
);

// Invoice types options selector
export const selectInvoiceTypeOptions = createSelector([selectInvoiceTypes], (invoiceTypes) =>
  Object.values(invoiceTypes).map((type) => ({
    value: type.uuid,
    label: type.name,
    icon: type.icon,
  })),
);

// Loading and error selectors
export const selectInvoiceLoading = (state: AppState) => selectInvoiceState(state).status === 'loading';
export const selectInvoiceError = (state: AppState) => selectInvoiceState(state).error;

// Pagination selectors
export const selectPaginatedInvoices = createSelector(
  [selectFilteredInvoices, selectInvoiceFilters],
  (invoices, filters) => {
    const page = filters?.page || 1;
    const perPage = filters?.perPage || 25;
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;

    return {
      data: invoices.slice(startIndex, endIndex),
      total: invoices.length,
      page,
      perPage,
      totalPages: Math.ceil(invoices.length / perPage),
    };
  },
);

// Recently created invoices selector
export const selectRecentInvoices = createSelector([selectInvoicesList], (invoices) =>
  invoices
    .filter((invoice) => {
      const createdDate = new Date(invoice.createdAt);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return createdDate >= thirtyDaysAgo;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5),
);

// Overdue invoices selector
export const selectOverdueInvoices = createSelector([selectInvoicesList], (invoices) => {
  const [today] = new Date().toISOString().split('T');
  return invoices.filter(
    (invoice) =>
      invoice.status !== InvoiceStatus.PAID && invoice.status !== InvoiceStatus.CANCELLED && invoice.dueDate < today,
  );
});

// Invoice counts by status
export const selectInvoiceCountsByStatus = createSelector([selectInvoicesList], (invoices) => {
  const counts: Record<InvoiceStatus, number> = {
    [InvoiceStatus.DRAFT]: 0,
    [InvoiceStatus.PENDING]: 0,
    [InvoiceStatus.SENT]: 0,
    [InvoiceStatus.PARTIALLY_PAID]: 0,
    [InvoiceStatus.PAID]: 0,
    [InvoiceStatus.OVERDUE]: 0,
    [InvoiceStatus.CANCELLED]: 0,
    [InvoiceStatus.REFUNDED]: 0,
  };

  invoices.forEach((invoice) => {
    counts[invoice.status]++;
  });

  return counts;
});

export default invoiceSlice.reducer;
